"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useState, useTransition } from "react"
import { useLocale, useTranslations } from "next-intl"
import { useQuery } from "@tanstack/react-query"
import { filterInitialQueryOptions, filterFacetsQueryOptions } from "@lib/queries/products"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { Button, cn } from "@/components/common"
import { Checkbox } from "@/components/ui/checkbox"
import { ChevronDown, SlidersHorizontal, X } from "lucide-react"

/** A top-level category and the children the panel shows under it when active. */
export type FilterCategory = {
  id: string
  handle: string
  name: string
  children?: { id: string; handle: string; name: string }[]
}

interface CatalogFiltersProps {
  sortBy: SortOptions
  activeTypeId?: string
  activeCollectionId?: string
  activeCategoryId?: string
  inStockOnly?: boolean
  collections: { id: string; title: string }[]
  /** Top-level categories, each with its children. Empty hides the section. */
  categories?: FilterCategory[]
  filters?: Record<string, string>
}

type LabeledFacetEntry = { value: string; label: string; count: number }

const PRODUCT_TYPES = [
  { id: "game", key: "games" },
  { id: "book", key: "books" },
  { id: "box", key: "boxes" },
] as const

const VISIBLE_COLLECTIONS = 5

const BINDING_KEY_MAP: Record<string, string> = {
  hardcover: "filters.bindingHardcover",
  paperback: "filters.bindingPaperback",
  softcover: "filters.bindingSoftcover",
}

const PLAY_TIME_BUCKETS = [
  { key: "0-30", labelKey: "filters.playTimeLt30" },
  { key: "30-60", labelKey: "filters.playTime3060" },
  { key: "60-120", labelKey: "filters.playTime60120" },
  { key: "120-9999", labelKey: "filters.playTimeGt120" },
] as const

// Mirrors backend PLAYER_BUCKETS — keep keys in sync.
const PLAYER_BUCKETS = [
  { key: "1", labelKey: "filters.playersSolo" },
  { key: "2", label: "2" },
  { key: "3-4", label: "3–4" },
  { key: "5+", label: "5+" },
] as const

// Mirrors backend AGE_BUCKETS — keep keys in sync.
const AGE_BUCKETS = [
  { key: "0-2", label: "0–2" },
  { key: "3-5", label: "3–5" },
  { key: "6-8", label: "6–8" },
  { key: "9-12", label: "9–12" },
  { key: "13+", label: "13+" },
] as const

const AGE_NOT_RATED_KEY = "not_rated"

export default function CatalogFilters({
  sortBy,
  activeTypeId,
  activeCollectionId,
  activeCategoryId,
  inStockOnly = false,
  collections,
  categories = [],
  filters = {},
}: CatalogFiltersProps) {
  const t = useTranslations("product")
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [mobileOpen, setMobileOpen] = useState(false)
  // Each filter toggle is a server navigation (RSC re-fetch of the product
  // grid). Wrap it in a transition so we can show a pending state instead of
  // the UI freezing silently until the server responds.
  const [isPending, startTransition] = useTransition()
  // Optimistic search params: reflect the click immediately in the checkboxes
  // while the server catches up. Reset to the real params once navigation
  // settles (props/searchParams update on the server round-trip).
  const [optimisticSearch, setOptimisticSearch] = useState<string | null>(null)
  useEffect(() => {
    // Server response arrived → drop the optimistic override.
    setOptimisticSearch(null)
  }, [searchParams])
  const effectiveSearch = optimisticSearch ?? searchParams.toString()

  // Optimistic view of the active selections, derived from effectiveSearch so
  // checkboxes/highlights flip the instant the user clicks — before the server
  // navigation settles. Facet COUNT queries below keep using the server-
  // confirmed props (counts can lag a beat without hurting clarity).
  const optimistic = useMemo(() => {
    const sp = new URLSearchParams(effectiveSearch)
    const f: Record<string, string> = {}
    for (const [k, v] of sp.entries()) f[k] = v
    return {
      typeId: sp.get("type") || undefined,
      collectionId: sp.get("collection") || undefined,
      categoryId: sp.get("category") || undefined,
      inStock: sp.get("inStock") === "true",
      filters: f,
    }
  }, [effectiveSearch])
  const oType = optimistic.typeId
  const oCollection = optimistic.collectionId
  const oCategory = optimistic.categoryId
  const oInStock = optimistic.inStock
  const oFilters = optimistic.filters

  // Count active filters for the badge
  const activeFilterCount = [
    oType,
    oCollection,
    oCategory,
    oFilters.content_language,
    oFilters.age_rating,
    oFilters.complexity,
    oFilters.min_players,
    oFilters.play_time,
    oFilters.game_type,
    oFilters.theme,
    oFilters.language_independent,
    oFilters.binding_type,
    oFilters.age_recommendation,
    oFilters.genre,
    oInStock ? "true" : null,
  ].filter(Boolean).length

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(effectiveSearch)
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) {
          params.delete(key)
        } else {
          params.set(key, value)
        }
      }
      params.delete("page")
      const next = params.toString()
      setOptimisticSearch(next)
      startTransition(() => router.push(`${pathname}?${next}`))
    },
    [effectiveSearch, pathname, router]
  )

  /**
   * Every filter off in one press.
   *
   * Clearing them one by one is one server navigation per filter, and after
   * three or four narrowing choices the reader had no way back to the full
   * catalogue short of editing the URL. Sort and the page's own params are NOT
   * filters and stay (`page` is dropped, as on any filter change).
   */
  const FILTER_PARAMS = [
    "type",
    "collection",
    "category",
    "inStock",
    "content_language",
    "language_independent",
    "min_players",
    "complexity",
    "age_rating",
    "play_time",
    "game_type",
    "theme",
    "binding_type",
    "age_recommendation",
    "genre",
  ]

  const clearAllFilters = useCallback(() => {
    const params = new URLSearchParams(effectiveSearch)
    for (const key of FILTER_PARAMS) params.delete(key)
    params.delete("page")
    const next = params.toString()
    setOptimisticSearch(next)
    startTransition(() => router.push(next ? `${pathname}?${next}` : pathname))
  }, [effectiveSearch, pathname, router])

  const handleTypeChange = useCallback(
    (newType: string | null) => {
      const params = new URLSearchParams(effectiveSearch)
      const gameFilterKeys = ["min_players", "complexity", "age_rating", "play_time", "game_type", "theme"]
      const bookFilterKeys = ["binding_type", "age_recommendation", "genre"]
      ;[...gameFilterKeys, ...bookFilterKeys].forEach(k => params.delete(k))
      if (newType) {
        params.set("type", newType)
      } else {
        params.delete("type")
      }
      params.delete("page")
      const next = params.toString()
      setOptimisticSearch(next)
      startTransition(() => router.push(`${pathname}?${next}`))
    },
    [effectiveSearch, pathname, router]
  )

  // Initial query: type counts + language facets (cross-filtered by
  // category, collection, inStock, content_language, language_independent).
  // Forwarding all "cross-type" filters keeps counter values in sync with
  // the URL when no specific product type is selected — the previous
  // version froze them at the first paint.
  const { data: initialData } = useQuery(
    filterInitialQueryOptions(
      locale,
      activeCategoryId,
      activeCollectionId,
      inStockOnly,
      filters.content_language,
      filters.language_independent
    )
  )

  // Type-specific facets with cross-filtering — include inStock so type
  // buckets shrink in sync with the toggle and the in_stock_count facet
  // reflects current filter context.
  const facetParams = useMemo(() => {
    if (!activeTypeId) return { type: "", locale }
    const p: Record<string, string> = { type: activeTypeId, locale }
    if (activeCategoryId) p.category_id = activeCategoryId
    if (activeCollectionId) p.collection_id = activeCollectionId
    if (inStockOnly) p.in_stock = "true"
    for (const [k, v] of Object.entries(filters)) {
      if (v) p[k] = v
    }
    return p
  }, [activeTypeId, activeCategoryId, activeCollectionId, filters, locale, inStockOnly])

  const { data: facetData } = useQuery(filterFacetsQueryOptions(facetParams))

  // Derived state.
  //
  // The fallback to `initialData` is only for the first paint and for the view
  // with NO type selected: that query counts every type together, so using it
  // while a type is selected shows numbers from the other types. That is what
  // «Подарки 5» beside a Boxes-only catalogue was — the count came from the
  // books, and clicking it emptied the grid. While a type is active the panel
  // waits for that type's own answer instead (null renders the label without a
  // number rather than with a wrong one).
  const sameTypeFacets = activeTypeId ? facetData?.facets : (facetData?.facets ?? initialData?.facets)
  const typeCounts = sameTypeFacets?.type_counts ?? initialData?.facets?.type_counts ?? null
  const langFacets = sameTypeFacets?.content_language ?? null
  const collectionCounts = sameTypeFacets?.collections ?? null
  const facets = activeTypeId ? (facetData?.facets ?? null) : null

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => { document.body.style.overflow = "" }
  }, [mobileOpen])

  const sortOptions: { value: SortOptions; label: string }[] = [
    { value: "created_at", label: t("store.latestArrivals") },
    { value: "price_asc", label: t("store.priceAsc") },
    { value: "price_desc", label: t("store.priceDesc") },
  ]

  const filterContent = (
    <div className="flex flex-col gap-5">
      {/* Reset — only when there is something to reset. */}
      {activeFilterCount > 0 && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {t("filters.activeCount", { count: activeFilterCount })}
          </span>
          <Button
            variant="ghost"
            onClick={clearAllFilters}
            className="h-auto rounded-full px-2.5 py-1 text-xs font-medium text-primary hover:bg-muted"
          >
            <X className="mr-1 h-3.5 w-3.5" />
            {t("filters.clearAll")}
          </Button>
        </div>
      )}

      {/* Product Type with counts */}
      <FilterSection title={t("filters.productType")}>
        <FilterOption active={!oType} onClick={() => handleTypeChange(null)}>
          {t("filters.allTypes")}
          {typeCounts && (
            <span className="text-muted-foreground ml-1">
              ({Object.values(typeCounts).reduce((a, b) => a + b, 0)})
            </span>
          )}
        </FilterOption>
        {PRODUCT_TYPES.map((type) => (
          <FilterOption
            key={type.id}
            active={oType === type.id}
            onClick={() => handleTypeChange(oType === type.id ? null : type.id)}
          >
            {t(`filters.${type.key}`)}
            {typeCounts?.[type.id] != null && (
              <span className="text-muted-foreground ml-1">
                ({typeCounts[type.id]})
              </span>
            )}
          </FilterOption>
        ))}
      </FilterSection>

      {/*
        * Categories.
        *
        * This used to be a row of pills above the grid, outside the panel. It
        * looked like a duplicate of the panel and was not one: the panel had no
        * category control at all, so those pills were the only way to narrow by
        * category — and being a row of CATEGORIES it could never show the boxes,
        * which are a product type. Moved in here, one filter in one place.
        *
        * The children of the active parent follow it, indented: a flat list of
        * every category would be eleven rows with nothing saying that
        * «Karetní hry» lives under «Deskové hry».
        */}
      {categories.length > 0 && (() => {
        const activeParent =
          categories.find((c) => c.id === oCategory) ??
          categories.find((c) => c.children?.some((ch) => ch.id === oCategory))
        return (
          <FilterSection title={t("filters.category")}>
            <FilterOption active={!oCategory} onClick={() => updateParams({ category: null })}>
              {t("store.allCategories")}
            </FilterOption>
            {categories.map((cat) => (
              <div key={cat.id}>
                <FilterOption
                  active={oCategory === cat.id}
                  onClick={() =>
                    updateParams({ category: oCategory === cat.id ? null : cat.id })
                  }
                >
                  {cat.name}
                </FilterOption>
                {activeParent?.id === cat.id &&
                  (cat.children ?? []).map((child) => (
                    <div key={child.id} className="pl-4">
                      <FilterOption
                        active={oCategory === child.id}
                        onClick={() =>
                          updateParams({
                            category: oCategory === child.id ? null : child.id,
                          })
                        }
                      >
                        {child.name}
                      </FilterOption>
                    </div>
                  ))}
              </div>
            ))}
          </FilterSection>
        )
      })()}

      {/* Collections — show only those with non-zero count under current filters */}
      {(() => {
        const visible = collections
          .map((c) => ({ ...c, count: collectionCounts?.[c.id] ?? null }))
          // If we have counts, drop empty collections; if we don't (initial paint), show all.
          .filter((c) => collectionCounts == null || (c.count ?? 0) > 0 || c.id === oCollection)
        if (visible.length === 0) return null
        return (
          <CollapsibleListSection
            title={t("filters.collection")}
            visibleCount={VISIBLE_COLLECTIONS}
            showMoreLabel={t("filters.showMore")}
            showLessLabel={t("filters.showLess")}
          >
            <FilterOption
              active={!oCollection}
              onClick={() => updateParams({ collection: null })}
            >
              {t("filters.allTypes")}
            </FilterOption>
            {visible.map((col) => (
              <FilterOption
                key={col.id}
                active={oCollection === col.id}
                onClick={() =>
                  updateParams({
                    collection: oCollection === col.id ? null : col.id,
                  })
                }
              >
                {col.title}
                {col.count != null && (
                  <span className="text-muted-foreground ml-1">({col.count})</span>
                )}
              </FilterOption>
            ))}
          </CollapsibleListSection>
        )
      })()}

      {/* Content Language */}
      {langFacets && Object.keys(langFacets).length > 0 && (
        <FilterSection title={t("filters.contentLanguage")}>
          <FilterOption
            active={!oFilters.content_language}
            onClick={() => updateParams({ content_language: null })}
          >
            {t("filters.allLanguages")}
          </FilterOption>
          {Object.entries(langFacets as Record<string, number>)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([lang, count]) => {
              if (count === 0) return null
              const current = oFilters.content_language?.split(",") || []
              const isActive = current.includes(lang)
              const labelKey = `filters.lang${lang.charAt(0).toUpperCase() + lang.slice(1)}` as any
              const fallback = lang.toUpperCase()
              const localized = t.has(labelKey) ? t(labelKey) : fallback
              return (
                <FilterCheckbox
                  key={lang}
                  label={`${localized} (${count})`}
                  checked={isActive}
                  onChange={() => {
                    const next = isActive
                      ? current.filter((v) => v !== lang)
                      : [...current, lang]
                    updateParams({
                      content_language: next.length > 0 ? next.join(",") : null,
                    })
                  }}
                />
              )
            })}
        </FilterSection>
      )}

      {/* Language independent filter (all types) */}
      {(() => {
        const count = facets?.language_independent_count ?? initialData?.facets?.language_independent_count ?? 0
        return count > 0 ? (
          <FilterSection title={t("filters.accessibility")}>
            <FilterCheckbox
              label={`${t("filters.languageIndependent")} (${count})`}
              checked={oFilters.language_independent === "true"}
              onChange={() =>
                updateParams({
                  language_independent: oFilters.language_independent === "true" ? null : "true",
                })
              }
            />
          </FilterSection>
        ) : null
      })()}

      {/* Game-specific filters */}
      {activeTypeId === "game" && facets && (
        <>
          {Object.keys(facets.age_rating || {}).length > 0 && (
            <CollapsibleFilterSection title={t("filters.ageRating")}>
              {[
                ...AGE_BUCKETS.map((b) => ({ key: b.key, label: b.label })),
                { key: AGE_NOT_RATED_KEY, label: t("filters.notRated") },
              ].map(({ key, label }) => {
                const count = (facets.age_rating as Record<string, number>)?.[key] || 0
                if (count === 0) return null
                const current = oFilters.age_rating?.split(",") || []
                const isActive = current.includes(key)
                return (
                  <FilterCheckbox
                    key={key}
                    label={`${label} (${count})`}
                    checked={isActive}
                    onChange={() => {
                      const next = isActive ? current.filter((v) => v !== key) : [...current, key]
                      updateParams({ age_rating: next.length > 0 ? next.join(",") : null })
                    }}
                  />
                )
              })}
            </CollapsibleFilterSection>
          )}
          {Object.keys(facets.complexity || {}).length > 0 && (
            <CollapsibleFilterSection title={t("filters.complexity")}>
              {[1, 2, 3, 4, 5].map((level) => {
                const count = (facets.complexity as Record<string, number>)?.[String(level)] || 0
                if (count === 0) return null
                const current = oFilters.complexity?.split(",").map(Number) || []
                const isActive = current.includes(level)
                return (
                  <FilterCheckbox
                    key={level}
                    label={`${"★".repeat(level)}${"☆".repeat(5 - level)} (${count})`}
                    checked={isActive}
                    onChange={() => {
                      const next = isActive ? current.filter((v) => v !== level) : [...current, level]
                      updateParams({ complexity: next.length > 0 ? next.join(",") : null })
                    }}
                  />
                )
              })}
            </CollapsibleFilterSection>
          )}
          {facets.player_counts && Object.keys(facets.player_counts).length > 0 && (
            <CollapsibleFilterSection title={t("filters.players")}>
              {PLAYER_BUCKETS.map((bucket) => {
                const count = (facets.player_counts as Record<string, number>)?.[bucket.key] || 0
                if (count === 0) return null
                const current = oFilters.min_players?.split(",") || []
                const isActive = current.includes(bucket.key)
                const display = "labelKey" in bucket ? t(bucket.labelKey as any) : bucket.label
                return (
                  <FilterCheckbox
                    key={bucket.key}
                    label={`${display} (${count})`}
                    checked={isActive}
                    onChange={() => {
                      const next = isActive ? current.filter((v) => v !== bucket.key) : [...current, bucket.key]
                      updateParams({ min_players: next.length > 0 ? next.join(",") : null })
                    }}
                  />
                )
              })}
            </CollapsibleFilterSection>
          )}
          {facets.play_time && Object.keys(facets.play_time).length > 0 && (
            <CollapsibleFilterSection title={t("filters.playTime")}>
              {PLAY_TIME_BUCKETS.map((bucket) => {
                const count = (facets.play_time as Record<string, number>)?.[bucket.key] || 0
                if (count === 0) return null
                const current = oFilters.play_time?.split(",") || []
                const isActive = current.includes(bucket.key)
                return (
                  <FilterCheckbox
                    key={bucket.key}
                    label={`${t(bucket.labelKey as any)} (${count})`}
                    checked={isActive}
                    onChange={() => {
                      const next = isActive ? current.filter((v) => v !== bucket.key) : [...current, bucket.key]
                      updateParams({ play_time: next.length > 0 ? next.join(",") : null })
                    }}
                  />
                )
              })}
            </CollapsibleFilterSection>
          )}
          {Array.isArray(facets.game_type) && facets.game_type.length > 0 && (
            <CollapsibleFilterSection title={t("filters.gameType")}>
              {(facets.game_type as LabeledFacetEntry[]).map(({ value, label, count }) => {
                const current = oFilters.game_type?.split(",") || []
                const isActive = current.includes(value)
                return (
                  <FilterCheckbox
                    key={value}
                    label={`${label} (${count})`}
                    checked={isActive}
                    onChange={() => {
                      const next = isActive ? current.filter((v) => v !== value) : [...current, value]
                      updateParams({ game_type: next.length > 0 ? next.join(",") : null })
                    }}
                  />
                )
              })}
            </CollapsibleFilterSection>
          )}
          {Array.isArray(facets.theme) && facets.theme.length > 0 && (
            <CollapsibleFilterSection title={t("filters.theme")}>
              {(facets.theme as LabeledFacetEntry[]).map(({ value, label, count }) => {
                const current = oFilters.theme?.split(",") || []
                const isActive = current.includes(value)
                return (
                  <FilterCheckbox
                    key={value}
                    label={`${label} (${count})`}
                    checked={isActive}
                    onChange={() => {
                      const next = isActive ? current.filter((v) => v !== value) : [...current, value]
                      updateParams({ theme: next.length > 0 ? next.join(",") : null })
                    }}
                  />
                )
              })}
            </CollapsibleFilterSection>
          )}
        </>
      )}

      {/* Book-specific filters */}
      {activeTypeId === "book" && facets && (
        <>
          {Object.keys(facets.binding_type || {}).length > 0 && (
            <CollapsibleFilterSection title={t("filters.bindingType")}>
              {Object.entries(facets.binding_type as Record<string, number>).map(([value, count]) => {
                const current = oFilters.binding_type?.split(",") || []
                const isActive = current.includes(value)
                const i18nKey = BINDING_KEY_MAP[value.toLowerCase()]
                const displayValue = i18nKey ? t(i18nKey as any) : value
                return (
                  <FilterCheckbox
                    key={value}
                    label={`${displayValue} (${count})`}
                    checked={isActive}
                    onChange={() => {
                      const next = isActive ? current.filter((v) => v !== value) : [...current, value]
                      updateParams({ binding_type: next.length > 0 ? next.join(",") : null })
                    }}
                  />
                )
              })}
            </CollapsibleFilterSection>
          )}
          {Object.keys(facets.age_recommendation || {}).length > 0 && (
            <CollapsibleFilterSection title={t("filters.ageRecommendation")}>
              {[
                ...AGE_BUCKETS.map((b) => ({ key: b.key, label: b.label })),
                { key: AGE_NOT_RATED_KEY, label: t("filters.notRated") },
              ].map(({ key, label }) => {
                const count = (facets.age_recommendation as Record<string, number>)?.[key] || 0
                if (count === 0) return null
                const current = oFilters.age_recommendation?.split(",") || []
                const isActive = current.includes(key)
                return (
                  <FilterCheckbox
                    key={key}
                    label={`${label} (${count})`}
                    checked={isActive}
                    onChange={() => {
                      const next = isActive ? current.filter((v) => v !== key) : [...current, key]
                      updateParams({ age_recommendation: next.length > 0 ? next.join(",") : null })
                    }}
                  />
                )
              })}
            </CollapsibleFilterSection>
          )}
          {Array.isArray(facets.genre) && facets.genre.length > 0 && (
            <CollapsibleFilterSection title={t("filters.genre")}>
              {(facets.genre as LabeledFacetEntry[]).map(({ value, label, count }) => {
                const current = oFilters.genre?.split(",") || []
                const isActive = current.includes(value)
                return (
                  <FilterCheckbox
                    key={value}
                    label={`${label} (${count})`}
                    checked={isActive}
                    onChange={() => {
                      const next = isActive ? current.filter((v) => v !== value) : [...current, value]
                      updateParams({ genre: next.length > 0 ? next.join(",") : null })
                    }}
                  />
                )
              })}
            </CollapsibleFilterSection>
          )}
        </>
      )}

      {/* Availability */}
      <FilterSection title={t("filters.availability")}>
        {(() => {
          const inStockCount =
            facetData?.facets?.in_stock_count ?? initialData?.facets?.in_stock_count
          const labelText = t("filters.inStockOnly")
          const label =
            typeof inStockCount === "number"
              ? `${labelText} (${inStockCount})`
              : labelText
          return (
            <FilterCheckbox
              label={label}
              checked={oInStock}
              onChange={() => updateParams({ inStock: oInStock ? null : "true" })}
            />
          )
        })()}
      </FilterSection>

      {/* Sort */}
      <FilterSection title={t("store.sortBy")}>
        {sortOptions.map((opt) => (
          <FilterOption
            key={opt.value}
            active={sortBy === opt.value}
            onClick={() => updateParams({ sortBy: opt.value })}
          >
            {opt.label}
          </FilterOption>
        ))}
      </FilterSection>
    </div>
  )

  return (
    <>
      {/* Top progress bar — visible while a filter navigation is in flight so
          the user knows results are loading instead of the UI feeling frozen. */}
      {isPending && (
        <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 overflow-hidden bg-primary/20 pointer-events-none">
          <div className="h-full w-1/3 bg-primary animate-[filterbar_1s_ease-in-out_infinite]" />
          <style>{`@keyframes filterbar{0%{transform:translateX(-100%)}100%{transform:translateX(400%)}}`}</style>
        </div>
      )}

      {/* Desktop sidebar */}
      <div
        aria-busy={isPending}
        /**
         * The filter column scrolls on its own, and never traps the page.
         *
         * `max-h` only limits a box that the sticky parent has already let grow
         * past the fold, so in a window shorter than the list the last filters
         * sat below the edge with nothing to scroll. A FIXED height (the column
         * is pinned anyway) makes the overflow real. And scroll chaining stays
         * ON: a sidebar is part of the page, so reaching its end must carry on
         * scrolling the page — `overscroll-contain` is what made the wheel feel
         * stuck.
         */
        className={cn(
          "hidden small:flex small:h-[calc(100dvh-7rem)] small:flex-col small:overflow-y-auto small:pr-2 scrollbar-thin transition-opacity",
          isPending && "opacity-60"
        )}
      >
        {filterContent}
      </div>

      {/* Mobile: floating filter button */}
      <Button
        variant="ghost"
        onClick={() => setMobileOpen(true)}
        className="h-auto p-0 rounded-none font-normal small:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-full shadow-lg hover:bg-primary-hover transition-colors"
      >
        <SlidersHorizontal className="h-4 w-4" />
        <span className="text-sm font-medium">{t("filters.filtersButton")}</span>
        {activeFilterCount > 0 && (
          <span className="ml-1 bg-primary-foreground text-primary text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {activeFilterCount}
          </span>
        )}
      </Button>

      {/* Mobile: bottom sheet drawer */}
      {mobileOpen && (
        <div className="small:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-2xl max-h-[85dvh] flex flex-col animate-in slide-in-from-bottom duration-300">
            {/* Handle + header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="text-base font-semibold font-serif">
                {t("filters.filtersButton")}
              </h2>
              <Button
                variant="ghost"
                onClick={() => setMobileOpen(false)}
                className="h-auto p-0 rounded-none font-normal p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
              {filterContent}
            </div>
            {/* Bottom action */}
            <div className="border-t border-border px-5 py-4">
              <Button
                variant="ghost"
                onClick={() => setMobileOpen(false)}
                className="h-auto p-0 rounded-none font-normal w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium text-sm hover:bg-primary-hover transition-colors"
              >
                {t("filters.showResults")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Sub-components ───

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  )
}

function CollapsibleFilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="ghost"
        onClick={() => setOpen(!open)}
        className="h-auto p-0 rounded-none font-normal flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground py-1 cursor-pointer hover:text-foreground transition-colors"
      >
        {title}
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </Button>
      {open && <div className="flex flex-col gap-0.5 pl-1">{children}</div>}
    </div>
  )
}

/**
 * Shows first N items, with a "Show more / Show less" toggle for the rest.
 */
function CollapsibleListSection({
  title,
  children,
  visibleCount,
  showMoreLabel,
  showLessLabel,
}: {
  title: string
  children: React.ReactNode
  visibleCount: number
  showMoreLabel: string
  showLessLabel: string
}) {
  const [expanded, setExpanded] = useState(false)
  const items = Array.isArray(children) ? children : [children]
  const allItems = items.flatMap((child) =>
    Array.isArray(child) ? child : [child]
  ).filter(Boolean)

  // +1 for the "All" option at the top
  const showCount = visibleCount + 1
  const hasMore = allItems.length > showCount
  const visibleItems = expanded ? allItems : allItems.slice(0, showCount)

  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <div className="flex flex-col gap-0.5">
        {visibleItems}
        {hasMore && (
          <Button
            variant="ghost"
            onClick={() => setExpanded(!expanded)}
            className="h-auto p-0 rounded-none font-normal text-xs text-primary hover:text-primary-hover transition-colors text-left px-2 py-1 cursor-pointer"
          >
            {expanded ? showLessLabel : `${showMoreLabel} (${allItems.length - showCount})`}
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * One row shell for every filter, whichever control it carries.
 *
 * The two kinds of row were built separately and lined up differently: a
 * `Button` is `justify-center` by the design system's own base class, so the
 * single-choice rows (type, category, collection, sort) had their label
 * centred, while the checkbox rows were a plain `<label>` starting at the left
 * edge. In one column, with the same indent, that reads as two different
 * panels. Same height, same padding, same hover, label always at the left; the
 * only difference left is the checkbox square, which is what tells the reader
 * this facet takes several answers at once.
 */
const FILTER_ROW = "flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm transition-colors"

function FilterOption({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        FILTER_ROW,
        "h-auto justify-start rounded-md font-normal cursor-pointer",
        active
          ? "bg-primary/10 text-primary font-medium hover:bg-primary/10"
          : "text-foreground hover:bg-muted"
      )}
    >
      {children}
    </Button>
  )
}

function FilterCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <label
      className={cn(
        FILTER_ROW,
        "cursor-pointer text-foreground hover:bg-muted",
        checked && "bg-primary/10 text-primary font-medium"
      )}
    >
      {/* The shared checkbox, not a bare <input>: the panel was the one place
          in the shop still styling a native box by hand. */}
      {/* The design system's own size: the indicator's tick is 16px, so a 14px
          box clipped it off-centre. */}
      <Checkbox checked={checked} onCheckedChange={() => onChange()} />
      <span className="flex-1">{label}</span>
    </label>
  )
}
